import { useEffect, useMemo, useRef, useState } from 'react'
import { CheckCircle2, ScanLine } from 'lucide-react'
import type { CreatedProduct } from '@/types/products'
import type { LabelPrintKind, ProductLabel } from '@/api/productLabelsApi'
import { useIssueLabels } from '@/hooks/useProductLabels'
import { LABEL_SIGNATURE_MEANINGS, issueItemFrom } from '@/lib/productLabels'
import { PrintLabelDialog, type PrintLabelRequest } from '@/components/labels/print/PrintLabelDialog'
import type { PrintProductChoices } from '@/api/labelPrintingApi'
import { VerifyLabelDialog } from '@/components/labels/VerifyLabelDialog'
import { Button } from '@/components/ui/button'
import { code128Svg, sanitizeForCode128 } from '@/lib/code128'
import { escapeHtml, printLabelViaIframe } from '@/lib/printLabel'
import { dinBarcodeData, formatDinForLabel } from '@/lib/isbtDin'
import { buildDigiTraxMessage, productLabelData, sendToDigiTrax, type ProductLabelSelections } from '@/lib/digitrax'

export const PRODUCT_LABEL_STYLE =
  'body{font-family:ui-sans-serif,system-ui,sans-serif;margin:0;padding:24px}' +
  '.label{border:2px solid #000;border-radius:4px;padding:14px 18px;width:320px}' +
  '.banner{font-size:11px;font-weight:700;text-align:center;letter-spacing:0.5px;margin-bottom:10px}' +
  '.caption{font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#555}' +
  '.value{font-size:14px;font-weight:700;margin-top:2px;word-break:break-all}' +
  '.row{margin-top:10px}' +
  // Messages read as label copy, not as a data field: normal weight, and
  // wrapping on words rather than break-all, which would split a sentence
  // mid-word the way it usefully does for a DIN.
  '.msg{font-size:11px;margin-top:1px;overflow-wrap:anywhere}' +
  '.barcode{margin-top:4px}'

export function labelInnerHtml(
  product: CreatedProduct,
  selections?: ProductLabelSelections,
): string {
  // ISBT 128 (z8nrz7fxwh): the barcode carries `=` + DIN + flag, and the text
  // reads `A9999 21 123456 | 01 | [3]` — the boxed check character is for
  // typing the DIN in by hand. An old-format DIN prints as it always did.
  const dinBarcode = code128Svg(sanitizeForCode128(dinBarcodeData(product.din, product.dinFlag)), {
    moduleWidth: 1.4,
    height: 32,
  })
  const codeBarcode = product.productDescriptionCode
    ? code128Svg(sanitizeForCode128(product.productDescriptionCode), { moduleWidth: 1.4, height: 32 })
    : ''
  return (
    `<div class="label">` +
    `<div class="banner">${product.patientMrn ? 'FOR PATIENT USE ONLY' : 'NOT PATIENT-SPECIFIC'}</div>` +
    `<div class="row"><div class="caption">Donation Identification Number</div>` +
    `<div class="value">${escapeHtml(formatDinForLabel(product.din, product.dinFlag))}</div>` +
    (dinBarcode ? `<div class="barcode">${dinBarcode}</div>` : '') +
    `</div>` +
    (product.patientMrn
      ? `<div class="row"><div class="caption">Intended Recipient</div>` +
        `<div class="value">${escapeHtml(product.patientName ?? '')}</div>` +
        `<div class="caption">MRN ${escapeHtml(product.patientMrn)}${product.patientDob ? ` · DOB ${escapeHtml(product.patientDob)}` : ''}</div>` +
        `</div>`
      : '') +
    `<div class="row"><div class="caption">Product Code</div>` +
    `<div class="value">${escapeHtml(product.productDescriptionCode || '—')}</div>` +
    (codeBarcode ? `<div class="barcode">${codeBarcode}</div>` : '') +
    `</div>` +
    selectionsHtml(selections) +
    `</div>`
  )
}

/**
 * The selections a product carries from its last print, in the shape the
 * label renderer wants. Returns undefined when nothing was ever recorded, so
 * a label printed before this existed simply renders without messages.
 */
export function storedSelectionsOf(product: CreatedProduct): ProductLabelSelections | undefined {
  const stored = product.labelSelections
  if (!stored) return undefined
  const free = stored.freeText ?? []
  return {
    labelType: stored.labelType as ProductLabelSelections['labelType'],
    bloodType: stored.bloodType ?? undefined,
    specialMessage: stored.specialMessage ?? undefined,
    intendedUse: stored.intendedUse ?? undefined,
    donationType: stored.donationType ?? undefined,
    freeText: [free[0], free[1], free[2], free[3]],
  }
}

/**
 * The messages chosen in Label Printing, rendered onto the label itself.
 *
 * These were previously assembled, sent to DigiTrax and then dropped, so the
 * operator confirmed a label that did not show what they had just typed —
 * the one thing on it that isn't derivable from the product record. Blank
 * lines are skipped rather than printed as empty rows, since the four
 * free-text fields are rarely all used.
 */
function selectionsHtml(selections?: ProductLabelSelections): string {
  if (!selections) return ''

  const coded = [
    ['Blood type', selections.bloodType],
    ['Special message', selections.specialMessage],
    ['Intended use', selections.intendedUse],
    ['Donation type', selections.donationType],
  ].filter((pair): pair is [string, string] => !!pair[1]?.trim())

  const free = (selections.freeText ?? []).filter(
    (line): line is string => !!line?.trim(),
  )

  if (coded.length === 0 && free.length === 0) return ''

  return (
    `<div class="row">` +
    coded
      .map(
        ([label, value]) =>
          `<div class="caption">${escapeHtml(label)}</div>` +
          `<div class="msg">${escapeHtml(value)}</div>`,
      )
      .join('') +
    (free.length > 0
      ? `<div class="caption">Message</div>` +
        free.map(line => `<div class="msg">${escapeHtml(line)}</div>`).join('')
      : '') +
    `</div>`
  )
}

/** The Label Printing choices as the print endpoint takes them: empty lines are "". */
export function printChoicesOf(selections: ProductLabelSelections | undefined): PrintProductChoices | undefined {
  if (!selections) return undefined
  return {
    labelType: selections.labelType,
    bloodType: selections.bloodType,
    specialMessage: selections.specialMessage,
    intendedUse: selections.intendedUse,
    donationType: selections.donationType,
    freeText: [0, 1, 2, 3].map(i => selections.freeText?.[i] ?? ''),
  }
}

/**
 * Label review and print for a product: the pre-print verification of
 * z8nrz7dt48 inside the print popup. Printing asks for a signature first: the
 * operator signs that the label is right, the label is recorded (numbered, on
 * the product's audit trail), and only then does it go to the printer that
 * Printer association names for product labels. The printed label is then
 * verified by scan, here or later from the labels list. The browser print
 * stays as "Print in browser", recorded the same way.
 */
export function ProductLabelPreviewDialog({
  product,
  selections,
  printKind = 'original',
  onClose,
}: {
  product: CreatedProduct | null
  /** What was chosen in Label Printing, shown so the preview matches the print. */
  selections?: ProductLabelSelections
  /** A first print, the same label again, or one with changed selections. */
  printKind?: LabelPrintKind
  onClose: () => void
}) {
  const { mutateAsync: issueLabels } = useIssueLabels()
  // The label this dialog recorded, offered for verification right away. A ref
  // too, so printing again from the same dialog records a reprint without
  // rebuilding the request (which would reset the popup).
  const [printed, setPrinted] = useState<ProductLabel | null>(null)
  const printedRef = useRef<ProductLabel | null>(null)
  const [verifying, setVerifying] = useState<ProductLabel | null>(null)

  useEffect(() => {
    setPrinted(null)
    printedRef.current = null
  }, [product?.id])

  const request = useMemo<PrintLabelRequest | null>(() => {
    if (!product) return null
    return {
      subject: 'product',
      ids: [product.id],
      title: `Product label · ${product.din}`,
      selections: printChoicesOf(selections),
      browser: {
        print: async () => {
          if (selections) await sendToDigiTrax(buildDigiTraxMessage(productLabelData(product, selections)))
          printLabelViaIframe(`Product ${product.din}`, labelInnerHtml(product, selections), PRODUCT_LABEL_STYLE)
        },
        previewHtml: [labelInnerHtml(product, selections)],
        previewStyle: PRODUCT_LABEL_STYLE,
      },
      record: {
        signatureTitle: 'Pre-print verification',
        signatureDescription: (
          <>
            Sign that the label for <strong>{product.din}</strong> is correct: DIN, recipient, product code and
            messages. It is recorded, then sent to the printer.
          </>
        ),
        meaning: LABEL_SIGNATURE_MEANINGS.prePrint,
        run: async (signingToken: string) => {
          // Printing again from the same dialog is a reprint of what was just printed.
          const kind: LabelPrintKind = printedRef.current ? 'reprint' : printKind
          const [label] = await issueLabels({
            items: [issueItemFrom(product.id, selections, kind)],
            signingToken,
            signatureMeaning: LABEL_SIGNATURE_MEANINGS.prePrint,
          })
          printedRef.current = label
          setPrinted(label)
          return [label.id]
        },
      },
    }
  }, [product, selections, printKind, issueLabels])

  return (
    <>
      <PrintLabelDialog request={request} onClose={onClose} hidden={!!verifying}>
        {printed && (
          <div className='flex items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800'>
            <span className='flex items-center gap-1.5'>
              <CheckCircle2 className='h-4 w-4' />
              {printed.usage === 'applied'
                ? `Label #${printed.number} verified after printing.`
                : `Label #${printed.number} recorded.`}
            </span>
            {printed.usage === 'pending' && (
              <Button size='sm' variant='outline' onClick={() => setVerifying(printed)}>
                <ScanLine className='mr-1.5 h-3.5 w-3.5' />
                Verify now
              </Button>
            )}
          </div>
        )}
      </PrintLabelDialog>

      <VerifyLabelDialog
        label={verifying}
        onClose={() => setVerifying(null)}
        onVerified={label => {
          printedRef.current = label
          setPrinted(label)
        }}
      />
    </>
  )
}
